import {
  Typography as MuiTypography,
  Divider as MuiDivider,
  Box as MuiBox,
  Button as MuiButton,
} from '@mui/material';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Chip, JamIcon } from 'components/ui';
import { getMainComponent } from 'lib/utils/metricComponents';
import { QuestionnaireResponseHistoryItem } from 'pages/PatientRecordPage/gqlSchemes/getQuestionnaireResponseHistory';
import VersionHistoryDropdown from './VersionHistoryDropdown';
import { formatNumber } from 'lib/utils';

interface QuestionnaireAnswerItemProps {
  questionnaireAnswer: QuestionnaireResponseHistoryItem;
  metricDefinitionId: string;
  onSeeAnswers: (
    response: QuestionnaireResponseHistoryItem,
    metricDefinitionId: string,
    isLatestVersion?: boolean
  ) => void;
  locale: any;
  patientId: string;
}

const QuestionnaireAnswerItem = ({
  questionnaireAnswer,
  metricDefinitionId,
  onSeeAnswers,
  locale,
  patientId
}: QuestionnaireAnswerItemProps) => {
  const { t } = useTranslation();
  // Use versionHistory from the questionnaireAnswer prop (includes all versions)
  const versionHistory = questionnaireAnswer?.versionHistory || [];
  const filteredVersionHistory = versionHistory.slice(1);

// <<<<<<< HEAD
//   useEffect(() => {
//     if (questionnaireAnswer?.id) {
//       loadVersionHistory({ variables: { id: questionnaireAnswer.id, patientId } });
//     }
//   }, [questionnaireAnswer?.id, loadVersionHistory]);
// =======
//   console.log('[DEBUG] QuestionnaireAnswerItem', {
//     questionnaireAnswer,
//     versionHistory,
//     filteredVersionHistory,
//   });
// >>>>>>> master

  if (!questionnaireAnswer?.id) {
    return (
      <SWrapper>
        <SMuiTypography variant="subtitle1" color="textSecondary">
          {t('Select a value to display')}
        </SMuiTypography>
      </SWrapper>
    );
  }

  const getScore = () => {
    if (
      questionnaireAnswer?.mainScoreMetric?.metricDefinitionId ===
      metricDefinitionId
    ) {
      return (
        getMainComponent(questionnaireAnswer?.mainScoreMetric?.components)
          ?.quantityValue?.value || '-'
      );
    }
    return (
      getMainComponent(
        questionnaireAnswer?.subScoreMetrics?.find(
          (subscore: any) => subscore?.metricDefinitionId === metricDefinitionId
        )?.components
      )?.quantityValue?.value || '-'
    );
  };

  return (
    <SWrapper>
      <MuiBox display="flex" alignItems="center">
        <MuiBox
          display="flex"
          flexDirection="column"
          textAlign="center"
          bgcolor="#f6f6f6"
          p="10px"
          borderRadius="5px"
        >
          <MuiTypography variant="h4">
            {formatNumber(getScore(), 0)}
          </MuiTypography>
          <MuiTypography variant="h5" color="textSecondary">
            {t('Score')}
          </MuiTypography>
        </MuiBox>

        <MuiDivider
          style={{ margin: '5px 20px' }}
          flexItem
          orientation="vertical"
        />
        {questionnaireAnswer?.mainScoreMetric?.createdAt && (
          <MuiBox>
            <MuiTypography variant="body1">
              {format(
                parseISO(questionnaireAnswer?.mainScoreMetric?.createdAt),
                'PPPp',
                {
                  locale,
                }
              )}
            </MuiTypography>
          </MuiBox>
        )}
        <MuiBox style={{ marginLeft: 'auto' }}>
          {questionnaireAnswer?.status === 'AMENDED' && (
            <Chip
              label={t('Amended')}
              color="primary"
              $color="#f8f8f8"
              $backgroundColor="#1AAE9F"
              style={{ marginRight: '10px' }}
            />
          )}
          <MuiButton
            style={{
              borderRadius: '25px',
              padding: '4px 12px',
            }}
            startIcon={
              <JamIcon size={20} icon="document" color="inherit" style={{}} />
            }
            onClick={() =>
              onSeeAnswers(questionnaireAnswer, metricDefinitionId, true)
            }
          >
            {t('See answers')}
          </MuiButton>
        </MuiBox>
      </MuiBox>

      {/* Version History for this specific answer */}
      <VersionHistoryDropdown
        versionHistory={filteredVersionHistory}
        metricDefinitionId={metricDefinitionId}
        onSeeAnswers={onSeeAnswers}
      />
    </SWrapper>
  );
};

const SWrapper = styled.div`
  margin: 30px 0 30px;
  flex-direction: column;
  width: 100%;
  display: flex;
`;

const SMuiTypography = styled(MuiTypography)`
  margin-top: 5px;
`;

export default QuestionnaireAnswerItem;
